import { useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  CloseIcon,
  SelectData,
  TransparentButton,
  Typography,
} from '@libs/components';
import { screenSizes } from '@libs/theme';

import { useDimensions, useTranslation } from 'src/hooks';
import {
  TransactionFiltersType,
  TransactionMultiSelectFilterValues,
} from 'src/types';

import { MultiSelectFilterDataType } from 'src/constants';
import { CustomFilter } from '../styles';

type TransactionMultiSelectFilterProps =
  MultiSelectFilterDataType<TransactionMultiSelectFilterValues> & {
    onFiltersSubmit: (newValue?: TransactionFiltersType) => void;
    onFilterClear: () => void;
    isFocused: boolean;
    handleFocus: () => void;
    handleBlur: () => void;
    options: SelectData<string>[];
    onOutsideClick?: () => void;
    renderOptionHeader?: (closeSelect: () => void) => JSX.Element;
    JustifyToRight?: boolean;
    icon?: string;
  };

export const TransactionMultiSelectFilter: React.FC<
  TransactionMultiSelectFilterProps
> = ({
  placeholder,
  textAfterSelection,
  selectedValues,
  handler,
  onFiltersSubmit,
  onFilterClear,
  options,
  onOutsideClick,
  renderOptionHeader,
  searchInputValue,
  JustifyToRight,
  icon,
}) => {
  const { t } = useTranslation();
  const { width } = useDimensions();
  const { colors } = useTheme();

  const defaultRenderValue = useCallback(
    (values?: SelectData<string>[]) => {
      if (width <= screenSizes.tablet) {
        const isSelected = values?.length !== 0;
        const handleClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          onFilterClear();
        };

        return (
          <SelectValueContainer>
            {icon && (
              <IconWrapper>
                <CustomImage src={icon} />
              </IconWrapper>
            )}
            <Typography
              type="bodyButton"
              fontFamily="primaryRegular"
              color={colors.text.default}
            >
              {textAfterSelection && values?.length
                ? textAfterSelection
                : placeholder}
            </Typography>
            {isSelected && (
              <CustomTransparentButton onClick={handleClick}>
                <CustomCloseIcon />
              </CustomTransparentButton>
            )}
          </SelectValueContainer>
        );
      }

      return (
        <FlexContainer>
          {icon && (
            <IconWrapper>
              <CustomImage src={icon} />
            </IconWrapper>
          )}
          <MultiSelectValueContainer>
            {(!!placeholder || !!textAfterSelection) && (
              <Typography
                color={colors.text.secondary}
                type="caption"
                className="title"
              >
                {t('common_all') && textAfterSelection
                  ? textAfterSelection
                  : placeholder}
              </Typography>
            )}
            <CustomTypography
              type="subHeadline"
              fontFamily="primaryMedium"
              className="value-class"
            >
              {values?.length && values.length !== options.length
                ? values.map(({ label }) => label).join(', ')
                : t('common_all')}
            </CustomTypography>
          </MultiSelectValueContainer>
        </FlexContainer>
      );
    },
    [
      width,
      icon,
      placeholder,
      textAfterSelection,
      colors,
      t,
      options.length,
      onFilterClear,
    ]
  );

  return (
    <CustomFilter
      renderOptionHeader={renderOptionHeader}
      placeholder={placeholder}
      renderValue={defaultRenderValue}
      textAfterSelection={textAfterSelection}
      selectedValues={selectedValues}
      onChange={handler}
      onFilterClear={onFilterClear}
      onFiltersSubmit={onFiltersSubmit}
      clearButtonText={t('common_clear')}
      filterButtonText={t('common_select')}
      options={options}
      emptyValueText={t('common_all')}
      onOutsideClick={onOutsideClick}
      searchInputValue={searchInputValue}
      JustifyToRight={JustifyToRight}
      isActive={!!selectedValues.length}
    />
  );
};

export const SelectValueContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  height: 100%;
`;
export const MultiSelectValueContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
export const CustomTypography = styled(Typography)`
  word-break: break-all;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
export const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`;
export const IconWrapper = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.highest};
  background-color: ${({ theme }) => theme.colors.interactive.brandColor};
  min-width: 3rem;
  min-height: 3rem;
  max-width: 3rem;
  max-height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.7rem;
  margin-right: 1.2rem;
`;
export const CustomImage = styled.img`
  width: 100%;
  object-fit: cover;
`;
export const CustomTransparentButton = styled(TransparentButton)`
  margin-left: 0.8rem;
`;
export const CustomCloseIcon = styled(CloseIcon)`
  width: 1rem;
  height: 1rem;
`;
